import React from 'react';
import { VisualAid } from '../../types';
import { MetricStairsCompact } from './MetricStairsCompact';
import { FractionPizza } from './FractionPizza';
import { FractionVisual } from './FractionVisual';
import { GeoGrid } from './GeoGrid';
import { NumberLine } from './NumberLine';
import { RatioTable } from './RatioTable';
import { RuleCard } from './RuleCard';
import { MoneyVisual } from './MoneyVisual';
import { ColumnVisual } from './ColumnVisual';
import { AlertTriangle } from 'lucide-react';
interface VisualAidFactoryProps {
  visualAid: VisualAid;
  isCorrect?: boolean;
}
export function VisualAidFactory({
  visualAid,
  isCorrect
}: VisualAidFactoryProps) {
  // FIXED: Enhanced error handling with detailed logging
  if (!visualAid) {
    console.error('❌ VISUAL AID ERROR: visualAid is null or undefined');
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1418">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1419" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1420">
          Visuele hulp ontbreekt
        </p>
      </div>;
  }
  if (!visualAid.type) {
    console.error('❌ VISUAL AID ERROR: visualAid.type is missing', visualAid);
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1421">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1422" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1423">
          Visuele hulp type ontbreekt
        </p>
      </div>;
  }
  if (!visualAid.data) {
    console.error('❌ VISUAL AID ERROR: visualAid.data is missing', visualAid);
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1424">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1425" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1426">
          Visuele hulp data ontbreekt
        </p>
      </div>;
  }
  const {
    type,
    data,
    showWhen
  } = visualAid;
  // Check if we should show this visual aid
  if (showWhen === 'correct' && !isCorrect) return null;
  if (showWhen === 'wrong' && isCorrect) return null;
  try {
    switch (type) {
      case 'stairs':
        // Validate required data for stairs
        if (!data.type || !data.from || !data.to) {
          console.error('❌ VISUAL AID ERROR: stairs missing required data', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1427">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1428" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1429">
                Metriek trap: ontbrekende gegevens
              </p>
            </div>;
        }
        return <MetricStairsCompact type={data.type || 'length'} from={data.from} to={data.to} value={data.value} highlight={data.highlight} showAnimation={data.showAnimation} data-id="element-1430" />;
      case 'fraction':
        // Validate required data for fractions
        if (data.usePizza !== false) {
          const numerator = data.numerator || Math.round((data.value || 0) * (data.denominator || 4));
          const denominator = data.denominator || 4;
          if (denominator === 0) {
            console.error('❌ VISUAL AID ERROR: fraction denominator is 0', data);
            return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1431">
                <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1432" />
                <p className="text-sm text-amber-800 font-medium" data-id="element-1433">
                  Breuk: ongeldige noemer (0)
                </p>
              </div>;
          }
          return <FractionPizza numerator={numerator} denominator={denominator} label={data.label} type={data.visualType || 'pizza'} showEaten={data.showEaten} data-id="element-1434" />;
        }
        return <FractionVisual {...data} data-id="element-1435" />;
      case 'geo':
        // Validate required data for geometry
        if (!data.width || !data.height) {
          console.error('❌ VISUAL AID ERROR: geo missing width or height', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1436">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1437" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1438">
                Geometrie: ontbrekende afmetingen
              </p>
            </div>;
        }
        return <GeoGrid {...data} data-id="element-1439" />;
      case 'numberline':
        return <NumberLine {...data} data-id="element-1440" />;
      case 'ratio':
        // Validate required data for ratio table
        if (!data.headers || !data.rows) {
          console.error('❌ VISUAL AID ERROR: ratio missing headers or rows', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1441">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1442" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1443">
                Tabel: ontbrekende gegevens
              </p>
            </div>;
        }
        return <RatioTable {...data} data-id="element-1444" />;
      case 'rule':
        // Validate required data for rule card
        if (!data.title || !data.content) {
          console.error('❌ VISUAL AID ERROR: rule missing title or content', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1445">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1446" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1447">
                Regel: ontbrekende titel of inhoud
              </p>
            </div>;
        }
        return <RuleCard {...data} data-id="element-1448" />;
      case 'money':
        // Validate required data for money visual
        if (data.amount === undefined || data.amount === null) {
          console.error('❌ VISUAL AID ERROR: money missing amount', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1449">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1450" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1451">
                Geld: ontbrekend bedrag
              </p>
            </div>;
        }
        return <MoneyVisual amount={data.amount} breakdown={data.breakdown} showCoins={data.showCoins !== false} showBills={data.showBills !== false} interactive={data.interactive} label={data.label} data-id="element-1452" />;
      case 'column':
        // Validate required data for column visual
        if (!data.operation) {
          console.error('❌ VISUAL AID ERROR: column missing operation', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1453">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1454" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1455">
                Kolomsgewijs: ontbrekende bewerking
              </p>
            </div>;
        }
        const operand1 = data.operand1 ?? data.numbers?.[0];
        const operand2 = data.operand2 ?? data.numbers?.[1];
        if (operand1 === undefined || operand2 === undefined) {
          console.error('❌ VISUAL AID ERROR: column missing operands', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1456">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1457" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1458">
                Kolomsgewijs: ontbrekende getallen
              </p>
            </div>;
        }
        return <ColumnVisual operation={data.operation} operand1={operand1} operand2={operand2} showSteps={data.showSteps !== false} currentStep={data.currentStep} animated={data.animated !== false} onStepChange={data.onStepChange} data-id="element-1459" />;
      default:
        console.warn(`❌ VISUAL AID ERROR: Unknown visual aid type "${type}"`);
        return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1460">
            <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1461" />
            <p className="text-sm text-amber-800 font-medium" data-id="element-1462">
              Onbekend visueel type: {type}
            </p>
          </div>;
    }
  } catch (error) {
    console.error('❌ VISUAL AID ERROR: Error rendering visual aid', error, visualAid);
    return <div className="p-4 bg-rose-50 rounded-lg border-2 border-rose-200 text-center" data-id="element-1463">
        <AlertTriangle className="w-6 h-6 text-rose-600 mx-auto mb-2" data-id="element-1464" />
        <p className="text-sm text-rose-800 font-medium" data-id="element-1465">
          Fout bij laden visuele hulp
        </p>
        <p className="text-xs text-rose-600 mt-1" data-id="element-1466">
          {error instanceof Error ? error.message : 'Onbekende fout'}
        </p>
      </div>;
  }
}